import { useEffect } from "react";
import { Download, PlusSquare, Share2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { usePwaInstallPrompt } from "../hooks/usePwaInstallPrompt";

/**
 * Invitación discreta a instalar Agricos como aplicación.
 *
 * Aparece abajo, como los avisos, y desaparece para siempre en cuanto molesta:
 * al cerrarla no vuelve en toda la sesión y guarda silencio un mes. Si la app ya
 * corre instalada no se monta nunca.
 *
 * En iPhone y iPad no existe `beforeinstallprompt`, así que en vez de un botón
 * se explica el gesto: Compartir → Agregar a pantalla de inicio.
 *
 * Se monta en `src/main.tsx`, junto a `<PwaUpdatePrompt />`.
 */
export function PwaInstallPrompt() {
  const { visible, variant, installing, accept, dismiss } = usePwaInstallPrompt();

  // Escape lo cierra, como cualquier aviso flotante.
  useEffect(() => {
    if (!visible) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, dismiss]);

  if (!visible || !variant) return null;

  // `aside` y no `dialog`: es una sugerencia que no atrapa el foco ni bloquea
  // la página; anunciarla como diálogo confundía a los lectores de pantalla.
  return (
    <aside
      aria-label="Instalar Agricos como aplicación"
      className={cn(
        "fixed inset-x-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-toast max-w-md",
        "rounded-xl border border-border bg-card p-3 shadow-xl animate-fade-in-up",
        // En escritorio, a la esquina inferior derecha: centrado tapaba el
        // contenido y parecia un dialogo del sistema, no una sugerencia.
        "md:bottom-5 md:left-auto md:right-5 md:w-96",
      )}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Download className="h-5 w-5" aria-hidden="true" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            Ten Agricos a un toque
          </p>

          {variant === "prompt" ? (
            <p className="mt-0.5 text-xs text-muted-foreground">
              Instálala para abrirla desde la pantalla de inicio, a pantalla
              completa, y ver los últimos datos aunque no haya señal.
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-muted-foreground">
              En Safari, toca{" "}
              <Share2
                className="inline h-3.5 w-3.5 align-text-bottom"
                aria-hidden="true"
              />{" "}
              <span className="font-medium text-foreground">Compartir</span> y
              después{" "}
              <PlusSquare
                className="inline h-3.5 w-3.5 align-text-bottom"
                aria-hidden="true"
              />{" "}
              <span className="font-medium text-foreground">
                “Agregar a pantalla de inicio”
              </span>
              . Es el único modo de recibir avisos en el iPhone.
            </p>
          )}

          {variant === "prompt" && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" disabled={installing} onClick={() => void accept()}>
                <Download className="h-4 w-4" aria-hidden="true" />
                {installing ? "Instalando…" : "Instalar"}
              </Button>
              <Button size="sm" variant="ghost" onClick={dismiss}>
                Ahora no
              </Button>
            </div>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="-mr-1 -mt-1 shrink-0"
          aria-label="Cerrar la invitación a instalar"
          onClick={dismiss}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </aside>
  );
}
